import { Signal } from '@preact/signals';
import type { HTMLAttributeAnchorTarget } from 'preact/compat';
import { STATUS_MESSAGE_TO_TRANSLATE_KEY, TYPE_BY_STATUS_MESSAGE, type StatusMessage } from './constants';
import { localize } from 'i18n/localize';

export type MessageFormat = {
	text: string;
	href?: string;
	target?: HTMLAttributeAnchorTarget;
};

export type ToastMessageParams = {
	id: string;
	messageParts: MessageFormat[];
	type: ToastMessageType;
};

export type ToastMessageType = 'success' | 'error' | 'info';

class ToastMessageSignal extends Signal<ToastMessageParams[]> {
	_id = 0;
	_predefinedMessages = localize().toastMessage;

	removeMessage(id: string) {
		this.value = this.value.filter((message) => message.id !== id);
	}

	addMessage(messageParts: string | MessageFormat[], type: ToastMessageType = 'info', timeoutMs = 5000) {
		const id = String(this._id++);

		if (typeof messageParts === 'string') {
			messageParts = [{ text: messageParts }];
		}

		this.value = [{ id, messageParts, type }, ...this.value];

		if (timeoutMs) {
			setTimeout(() => {
				this.removeMessage(id);
			}, timeoutMs);
		}
	}

	addPredefinedMessage(messageKey: StatusMessage, timeoutMs = 5000) {
		const id = String(this._id++);

		const messageText =
			this._predefinedMessages[
				STATUS_MESSAGE_TO_TRANSLATE_KEY[
					messageKey as keyof typeof STATUS_MESSAGE_TO_TRANSLATE_KEY
				] as keyof typeof this._predefinedMessages
			];
		const messageParts: MessageFormat[] = [{ text: messageText }];

		this.value = [{ id, messageParts, type: TYPE_BY_STATUS_MESSAGE[messageKey] }, ...this.value];

		if (timeoutMs) {
			setTimeout(() => {
				this.removeMessage(id);
			}, timeoutMs);
		}
	}

	success(message: string | MessageFormat[], timeoutMs = 5000) {
		this.addMessage(message, 'success', timeoutMs);
	}

	error(message: string | MessageFormat[], timeoutMs = 5000) {
		this.addMessage(message, 'error', timeoutMs);
	}

	info(message: string | MessageFormat[], timeoutMs = 5000) {
		this.addMessage(message, 'info', timeoutMs);
	}

	oops(
		message: string | MessageFormat[] = 'Něco se pokazilo... Stránka nemusí správně fungovat.',
		timeoutMs = 10000,
	) {
		this.addMessage(message, 'error', timeoutMs);
	}
}

export const toastMessages = new ToastMessageSignal([]);
